import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Ban, Loader2, RefreshCw, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";

const RES = { reussie: ["Réussie", "text-emerald-700"], echouee: ["Échouée", "text-amber-700"], refusee: ["Refusée", "text-red-600"], verification: ["Code demandé", "text-sky-700"] };
export const appareil = (ua) => {
  const os = /iPhone|iPad/.test(ua) ? "iPhone/iPad" : /Android/.test(ua) ? "Android" : /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "";
  const nav = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : ua ? "Autre" : "—";
  return [nav, os].filter(Boolean).join(" · ");
};

const Bloquees = ({ liste, onDebloquer, onBloquer, onToutDebloquer }) => {
  const [ip, setIp] = useState("");
  return (
    <div className="mt-6" data-testid="ip-bloquees">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-slate-800">IP bloquées ({liste.length})</p>
        {liste.length > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={onToutDebloquer} data-testid="ip-tout-debloquer"><Trash2 className="h-3 w-3 mr-1" />Tout débloquer</Button>}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {liste.map((b) => (
          <span key={b.ip} className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs text-red-700" data-testid={`ip-bloquee-${b.ip}`}>
            {b.ip}{b.note && ` — ${b.note}`}
            <button type="button" className="underline" onClick={() => onDebloquer(b.ip)} data-testid={`ip-debloquer-${b.ip}`}>Débloquer</button>
          </span>
        ))}
        {!liste.length && <span className="text-xs text-slate-500">Aucune IP bloquée.</span>}
      </div>
      <div className="mt-3 flex gap-2 max-w-md">
        <Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="Bloquer une IP (ex. 203.0.113.7)" className="h-9" data-testid="ip-bloquer-input" />
        <Button size="sm" variant="outline" className="h-9" disabled={!ip.trim()} onClick={() => { onBloquer(ip.trim(), "Bloquée manuellement"); setIp(""); }} data-testid="ip-bloquer-button"><Ban className="h-3.5 w-3.5 mr-1" />Bloquer</Button>
      </div>
    </div>
  );
};

export const ConnexionsPanel = () => {
  const [d, setD] = useState(null);
  const charger = useCallback(() => api.get("/connexions").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (d && window.location.hash === "#connexions") { document.getElementById("connexions")?.scrollIntoView({ behavior: "smooth" }); }
  }, [d]);
  const bloquer = async (ip, note = "Bloquée depuis le journal") => {
    try { await api.post("/ip-bloquees", { ip, note }); toast.success(`IP ${ip} bloquée`); charger(); } catch (e) { toast.error(errMsg(e)); }
  };
  const debloquer = async (ip) => {
    try { await api.delete(`/ip-bloquees/${encodeURIComponent(ip)}`); toast.success(`IP ${ip} débloquée`); charger(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toutDebloquer = async () => {
    if (!window.confirm("Débloquer toutes les adresses IP ?")) { return; }
    try { const { data } = await api.delete("/ip-bloquees"); toast.success(`${data.debloquees} IP débloquée(s)`); charger(); } catch (e) { toast.error(errMsg(e)); }
  };
  const supprimer = async (c) => {
    try { await api.delete(`/connexions/${c.id}`); toast.success("Ligne supprimée du journal"); charger(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toutSupprimer = async () => {
    if (!window.confirm("Supprimer tout le journal des connexions ? Cette action est définitive.")) { return; }
    try { const { data } = await api.delete("/connexions"); toast.success(`${data.supprimees} ligne(s) supprimée(s)`); charger(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />;
  const bloquees = new Set(d.bloquees.map((b) => b.ip));
  return (
    <Panel title="Journal des connexions" testId="connexions-panel"
      action={<div className="flex gap-1">
        {d.admin && d.connexions.length > 0 && <Button size="sm" variant="ghost" className="text-red-600" onClick={toutSupprimer} data-testid="connexions-tout-supprimer"><Trash2 className="h-3.5 w-3.5 mr-1" />Tout supprimer</Button>}
        <Button size="sm" variant="ghost" onClick={charger} data-testid="connexions-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Actualiser</Button>
      </div>}>
      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />{d.admin ? "Toutes les tentatives de connexion" : "Vos tentatives de connexion"} (200 dernières). Ville et pays approximatifs, d'après l'IP.</p>
      <div className="overflow-x-auto max-h-[420px]">
        <SmartTable id="connexions" className="w-full text-sm min-w-[860px]" data-testid="connexions-table">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Résultat</th><th className="py-2 pr-3">IP</th><th className="py-2 pr-3">Ville</th><th className="py-2 pr-3">Pays</th><th className="py-2 pr-3">Appareil</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {d.connexions.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`connexion-${c.id}`}>
                <td className="py-2 pr-3 whitespace-nowrap">{new Date(c.created_at).toLocaleString("fr-CA")}</td>
                <td className={`py-2 pr-3 ${RES[c.resultat]?.[1] || ""}`} title={c.raison}>{RES[c.resultat]?.[0] || c.resultat}{c.raison && c.resultat !== "reussie" && <span className="block text-xs text-slate-500">{c.raison}</span>}{c.alerte_envoyee && <span className="block text-xs text-amber-700" title={c.alerte_motif} data-testid={`connexion-alerte-${c.id}`}>Alerte envoyée</span>}</td>
                <td className="py-2 pr-3 font-mono text-xs">{c.ip}</td>
                <td className="py-2 pr-3">{c.ville || "—"}</td><td className="py-2 pr-3">{c.pays || "…"}</td>
                <td className="py-2 pr-3 text-xs text-slate-600" title={c.appareil}>{appareil(c.appareil)}</td>
                <td className="py-2 text-right whitespace-nowrap">{c.ip === d.mon_ip ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700" data-testid={`connexion-mon-ip-${c.id}`}>Votre IP</span> : !d.admin ? null : bloquees.has(c.ip) ? <span className="text-xs text-red-600">Bloquée</span> :
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => bloquer(c.ip)} data-testid={`connexion-bloquer-${c.id}`}><Ban className="h-3 w-3 mr-1" />Bloquer</Button>}
                  {d.admin && <Button size="icon" variant="ghost" className="ml-1 h-7 w-7 text-slate-400 hover:text-red-600" title="Supprimer cette ligne" onClick={() => supprimer(c)} data-testid={`connexion-supprimer-${c.id}`}><X className="h-3.5 w-3.5" /></Button>}</td>
              </tr>
            ))}
            {!d.connexions.length && <tr><td colSpan={7} className="py-4 text-sm text-slate-500">Aucune connexion enregistrée pour l'instant.</td></tr>}
          </tbody>
        </SmartTable>
      </div>
      {d.admin && <Bloquees liste={d.bloquees} onBloquer={bloquer} onDebloquer={debloquer} onToutDebloquer={toutDebloquer} />}
    </Panel>
  );
};
